import React, { useState, useEffect } from "react";
import { Button, FormInput, FormSelect } from "../../../components/ui";
import { formatDateTransaction } from "../../../helper/formatDate";
import { useDoctorAutocomplete } from "../../../hooks/doctor/useDoctorAutocomplete";
import { useDebounce } from "../../../hooks/useDebounce";

import "./salesFilter.css";

// move option-list to prevent rerendering
const listShift = [
  { index: "", value: "Semua Shift" },
  { index: "0", value: "Pagi" },
  { index: "1", value: "Siang" },
];

const listPrescriptionType = [
  { index: "", value: "Semua Jenis Obat" },
  { index: "prescription", value: "Full Obat Resep" },
  { index: "non-prescription", value: "Full Obat Bebas" },
];

const dateFilterTypes = {
  daily: "date",
  monthly: "month",
  yearly: "number",
}; // as const

/**
 * @param {keyof dateFilterTypes} typeOfSales
 */
const getDateFilter = (typeOfSales) => {
  return dateFilterTypes[typeOfSales] ?? dateFilterTypes.yearly;
};

/**
 * @typedef {Object} SalesFilterData
 * @property {string} date
 * @property {{ index: string, value: string }} shift
 * @property {{ index: string, value: string }}
 * @property {{ doctor_id: string, doctor_name: string }} doctor
 */

/**
 * @typedef {Object} SalesFilterRefactorProps
 * @property {keyof dateFilterTypes} typeOfSales
 * @property {boolean} hideDoctorFilter
 * @property {React.Dispatch<React.SetStateAction<SalesFilterData>>} setFilterData
 */

/**
 * @param {SalesFilterRefactorProps} props
 */
export default function SalesFilter({
  typeOfSales,
  setFilterData,
  hideDoctorFilter = false,
}) {
  // =====================
  // Constant Definition
  // =====================
  const [dateInput, setDateInput] = useState("");
  const [selectedShift, setSelectedShift] = useState({
    index: "",
    value: "Semua Shift",
  });
  const [doctorData, setDoctorData] = useState({
    doctor_id: "",
    doctor_name: "",
  });
  const [doctorInput, setDoctorInput] = useState("");

  const [selectedPrescriptionType, setSelectedPrescriptionType] = useState({
    index: "",
    value: "Semua Jenis Obat",
  });

  // =====================
  // Request Method
  // =====================
  const debouncedDoctorInput = useDebounce(doctorInput.toLowerCase());
  const suggestionDoctor = useDoctorAutocomplete(
    debouncedDoctorInput,
    !doctorData.doctor_id && !doctorData.doctor_name,
  );

  // =====================
  // Method Definition
  // =====================

  const handleShift = (event) => {
    const selectedShiftObj = listShift.find(
      (shift) => shift.index === event.target.value,
    );
    setSelectedShift(selectedShiftObj);
  };

  const selectDoctor = (doctor) => {
    setDoctorData({
      doctor_id: doctor.doctor_id,
      doctor_name: doctor.doctor_name,
    });
    setDoctorInput(doctor.doctor_name);
    suggestionDoctor.reset();
  };

  const onFilterClicked = () => {
    const queryJson = {
      date: formatDateTransaction(dateInput, typeOfSales),
      shift: selectedShift,
      prescriptionType: selectedPrescriptionType,
      doctor: doctorData,
    };
    setFilterData(queryJson);
  };

  const handlePrescriptionType = (event) => {
    const selectedTypeObj = listPrescriptionType.find(
      (type) => type.index === event.target.value,
    );
    setSelectedPrescriptionType(selectedTypeObj);
  };

  // =====================
  // Hook Definition
  // =====================

  useEffect(() => {
    const getInitialDate = () => {
      const today = new Date();
      const indonesiaTime = new Date(today.getTime() + 7 * 60 * 60 * 1000);

      if (typeOfSales === "daily") {
        return indonesiaTime.toISOString().split("T")[0];
      } else if (typeOfSales === "monthly") {
        return `${indonesiaTime.getFullYear()}-${String(
          indonesiaTime.getMonth() + 1,
        ).padStart(2, "0")}`;
      } else if (typeOfSales === "yearly") {
        return `${indonesiaTime.getFullYear()}`;
      }
      return "";
    };
    setDateInput(getInitialDate());
  }, [typeOfSales]);

  return (
    <div className="input-filter-transaction">
      <h4 className="mt-0 mb-4">Filter Dengan: </h4>

      <div className="mb-3 filter-input-field-container">
        <div style={{ position: "relative" }}>
          {typeOfSales === "daily" || typeOfSales === "monthly" ? (
            <FormInput
              type={getDateFilter(typeOfSales)}
              value={dateInput}
              onChange={(e) => setDateInput(e.target.value)}
            />
          ) : (
            <FormInput
              placeholder="Select year"
              type="number"
              value={dateInput}
              min="1900"
              max="2100"
              onChange={(e) => setDateInput(e.target.value)}
            />
          )}
        </div>

        <div style={{ position: "relative" }}>
          <FormSelect value={selectedShift.index} onChange={handleShift}>
            {listShift.map((shift) => (
              <option key={shift.index} value={shift.index}>
                {shift.value}
              </option>
            ))}
          </FormSelect>
        </div>

        <div style={{ position: "relative" }}>
          <FormSelect
            value={selectedPrescriptionType.index}
            onChange={handlePrescriptionType}
          >
            {listPrescriptionType.map((type) => (
              <option key={type.index} value={type.index}>
                {type.value}
              </option>
            ))}
          </FormSelect>
        </div>

        {!hideDoctorFilter ? (
          <div style={{ position: "relative" }}>
            <FormInput
              type="text"
              value={doctorInput}
              onChange={(e) => {
                setDoctorInput(e.target.value);
                setDoctorData({
                  doctor_id: "",
                  doctor_name: "",
                });
              }}
              placeholder="Masukkan dokter"
            />
            {suggestionDoctor.data.length > 0 && (
              <ul className="dropdown-list">
                {suggestionDoctor.data.map((doctor) => (
                  <li key={doctor.doctor_id}>
                    <button
                      className="dropdown-button"
                      onClick={() => {
                        selectDoctor(doctor);
                      }}
                    >
                      {doctor.doctor_name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>

      <div className="flex justify-end">
        <Button type="button" variant="info" onClick={onFilterClicked}>
          Cari penjualan
        </Button>
      </div>
    </div>
  );
}
